import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { Episode, FrameRecord } from '@erectus/shared';
import { STATE_LABEL, STATE_STYLE } from '@/scoring/frameState.js';
import { Icon } from '@/ui/icons.js';
import { Pill } from '@/ui/primitives.js';
import { bucketStates } from './Timeline.js';
import {
  ANNOUNCE_SKIP_MS,
  HEAD_PER_TORSO,
  BONES,
  REPLAY_SPEEDS,
  adjacentEpisode,
  alignAtHip,
  jointsOf,
  momentAt,
  referenceFrame,
  skipAwayFrom,
  stageFor,
  type Joints,
  type Point,
  type ReplaySpeed,
  type ReplayStage,
} from './replayModel.js';

/**
 * Skeleton replay (PRODUCT §2 screen 6): the session played back as the
 * camera-side skeleton, sped up so two hours take two minutes. Redrawn from
 * the landmarks on this phone — there is no video to replay (CLAUDE.md
 * principle 1).
 *
 * Away time is skipped while playing; the scrubber can still land in it.
 */
export function Replay({
  frames,
  episodes,
  startedAt,
  rollDeg,
  startAt,
  autoPlay,
  initialSpeed,
  onClose,
}: {
  frames: readonly FrameRecord[];
  episodes: readonly Episode[];
  startedAt: number;
  /** The session's median phone roll (summary.phoneRollDeg); 0 when unknown. */
  rollDeg: number;
  startAt: number;
  autoPlay: boolean;
  initialSpeed: ReplaySpeed;
  onClose(): void;
}): ReactNode {
  const stage = useMemo(() => stageFor(frames, rollDeg), [frames, rollDeg]);
  const reference = useMemo(() => {
    const frame = referenceFrame(frames);
    return frame && stage ? jointsOf(frame.lm, stage.side, stage.rollDeg) : null;
  }, [frames, stage]);
  const strip = useMemo(
    () => bucketStates(frames, (frames[frames.length - 1]?.t ?? 0) / 1000),
    [frames],
  );
  const endT = Math.max(1, frames[frames.length - 1]?.t ?? 1);

  const [t, setT] = useState(() => Math.min(startAt, endT));
  const [playing, setPlaying] = useState(autoPlay);
  const [speed, setSpeed] = useState<ReplaySpeed>(initialSpeed);
  const [showReference, setShowReference] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const tRef = useRef(t);

  const seek = useCallback(
    (next: number): void => {
      const clamped = Math.max(0, Math.min(endT, next));
      tRef.current = clamped;
      setT(clamped);
    },
    [endT],
  );

  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    let last = performance.now();
    const step = (now: number): void => {
      // A backgrounded tab resumes with a huge gap; never leap more than a tenth of a second of wall time.
      const wall = Math.min(100, now - last);
      last = now;
      let next = tRef.current + wall * speed;
      const resume = skipAwayFrom(frames, next);
      if (resume !== null) {
        if (resume - next >= ANNOUNCE_SKIP_MS) setNotice(`Skipped ${Math.round((resume - next) / 60_000)} min away from the desk`);
        next = resume;
      }
      if (next >= endT) {
        seek(endT);
        setPlaying(false);
        return;
      }
      seek(next);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [playing, speed, frames, endT, seek]);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 1800);
    return () => clearTimeout(timer);
  }, [notice]);

  // Full-screen: the page behind must not scroll, and Escape closes.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
      if (event.key === ' ') {
        event.preventDefault();
        setPlaying((p) => !p);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const moment = stage ? momentAt(frames, t, stage.side, stage.rollDeg) : null;
  const state = moment?.state ?? 'invalid';
  const style = STATE_STYLE[state];
  const prevSlip = adjacentEpisode(episodes, t, -1);
  const nextSlip = adjacentEpisode(episodes, t, 1);

  const togglePlay = (): void => {
    if (!playing && tRef.current >= endT) seek(0);
    setPlaying((p) => !p);
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Session replay"
      className="fixed inset-0 z-50 flex flex-col bg-surface text-ink"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="mx-auto flex min-h-0 w-full max-w-screen-sm flex-1 flex-col gap-3 px-4 py-3">
        <header className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold">Replay</h2>
            <p className="text-xs text-muted tabular-nums">
              {new Date(startedAt + t).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} ·{' '}
              {clock(t)} of {clock(endT)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close replay"
            className="grid size-11 place-items-center rounded-full bg-card text-ink-soft shadow-sm"
          >
            <Icon name="close" />
          </button>
        </header>

        <div className="relative min-h-0 flex-1 overflow-hidden rounded-2xl border border-line bg-card">
          <div
            className="absolute top-3 left-3 z-10 flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold"
            style={{ backgroundColor: style.soft, color: style.color }}
          >
            <Icon name={style.icon} size={18} />
            {STATE_LABEL[state]}
          </div>
          {notice ? (
            <p
              role="status"
              className="absolute inset-x-6 bottom-3 z-10 rounded-xl bg-ink/85 px-3 py-2 text-center text-xs font-medium text-white"
            >
              {notice}
            </p>
          ) : null}
          {stage ? (
            <Figure
              stage={stage}
              joints={moment?.joints ?? null}
              reference={showReference ? reference : null}
              color={style.color}
              cva={moment?.cva ?? null}
              trunk={moment?.trunk ?? null}
            />
          ) : (
            <p className="grid h-full place-items-center px-6 text-center text-sm text-muted">
              Nothing in this session was measurable enough to draw.
            </p>
          )}
        </div>

        {reference ? (
          <label className="flex items-center gap-2 text-xs text-ink-soft">
            <input
              type="checkbox"
              checked={showReference}
              onChange={(event) => setShowReference(event.target.checked)}
              className="size-4 accent-[color:var(--color-ink)]"
            />
            <span>
              <span className="font-semibold">Faint figure</span>: you sitting well earlier in this session ·{' '}
              <span className="font-semibold">solid</span>: this moment
            </span>
          </label>
        ) : null}

        <Scrubber strip={strip} t={t} endT={endT} onSeek={seek} />

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <TransportButton
              label="Previous slip"
              icon="prevMark"
              disabled={prevSlip === null}
              onClick={() => prevSlip !== null && seek(prevSlip)}
            />
            <button
              type="button"
              onClick={togglePlay}
              aria-label={playing ? 'Pause' : 'Play'}
              className="grid size-14 place-items-center rounded-full bg-ink text-white"
            >
              <Icon name={playing ? 'pause' : 'play'} size={24} strokeWidth={2.4} />
            </button>
            <TransportButton
              label="Next slip"
              icon="nextMark"
              disabled={nextSlip === null}
              onClick={() => nextSlip !== null && seek(nextSlip)}
            />
          </div>
          <div className="flex gap-1" role="group" aria-label="Playback speed">
            {REPLAY_SPEEDS.map((s) => (
              <Pill key={s} active={s === speed} className="px-2.5 tabular-nums" onClick={() => setSpeed(s)}>
                {s}×
              </Pill>
            ))}
          </div>
        </div>
        <p className="-mt-1 text-right text-[11px] text-muted tabular-nums">
          Whole session in about {clock(endT / speed)} at {speed}×
        </p>
      </div>
    </div>,
    document.body,
  );
}

function TransportButton({
  label,
  icon,
  disabled,
  onClick,
}: {
  label: string;
  icon: 'prevMark' | 'nextMark';
  disabled: boolean;
  onClick(): void;
}): ReactNode {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="grid size-11 place-items-center rounded-full text-ink-soft disabled:opacity-30"
    >
      <Icon name={icon} />
    </button>
  );
}

/** The session's state strip with a playhead, and a slider laid over it for seeking. */
function Scrubber({
  strip,
  t,
  endT,
  onSeek,
}: {
  strip: ReturnType<typeof bucketStates>;
  t: number;
  endT: number;
  onSeek(t: number): void;
}): ReactNode {
  const at = (t / endT) * 100;
  return (
    <div>
      <div className="relative h-8">
        <div className="absolute inset-x-0 top-2 flex h-4 gap-px overflow-hidden rounded-md" aria-hidden>
          {strip.map((state, i) => (
            <span
              key={i}
              className="h-full flex-1"
              style={{ backgroundColor: STATE_STYLE[state].color, opacity: state === 'invalid' ? 0.3 : 1 }}
            />
          ))}
        </div>
        <span
          aria-hidden
          className="absolute top-0 h-8 w-1 -translate-x-1/2 rounded-full bg-ink ring-2 ring-white"
          style={{ left: `${at}%` }}
        />
        <input
          type="range"
          min={0}
          max={endT}
          step={1000}
          value={t}
          onChange={(event) => onSeek(Number(event.target.value))}
          aria-label="Position in the session"
          aria-valuetext={clock(t)}
          className="absolute inset-0 h-8 w-full cursor-pointer opacity-0"
        />
      </div>
      <div className="mt-0.5 flex justify-between text-[11px] text-muted tabular-nums">
        <span>0:00</span>
        <span>{clock(endT)}</span>
      </div>
    </div>
  );
}

// ── the figure ────────────────────────────────────────────────────────────

const W = 300;
const H = 360;
const PAD = 36;

function projector(stage: ReplayStage): { project(p: Point): { x: number; y: number }; headR: number } {
  const { minX, maxX, minY, maxY } = stage.bounds;
  const scale = Math.min((W - 2 * PAD) / Math.max(1e-3, maxX - minX), (H - 2 * PAD) / Math.max(1e-3, maxY - minY));
  const offX = (W - (maxX - minX) * scale) / 2;
  const offY = (H - (maxY - minY) * scale) / 2;
  // In proportion to the body: a fixed radius read as a pinhead once the legs
  // were in view and the figure was zoomed out.
  const headR = stage.torso === null ? 13 : Math.min(48, Math.max(13, stage.torso * scale * HEAD_PER_TORSO));
  return {
    project: (p) => {
      const x = (p.x - minX) * scale + offX;
      return { x: stage.facingLeft ? W - x : x, y: (p.y - minY) * scale + offY };
    },
    headR,
  };
}

function Figure({
  stage,
  joints,
  reference,
  color,
  cva,
  trunk,
}: {
  stage: ReplayStage;
  joints: Joints | null;
  reference: Joints | null;
  color: string;
  cva: number | null;
  trunk: number | null;
}): ReactNode {
  const { project, headR } = useMemo(() => projector(stage), [stage]);

  if (!joints) {
    return (
      <div className="grid h-full place-items-center px-6 text-center text-sm text-muted">
        Out of view here — away from the desk, or the camera could not see the side of the body.
      </div>
    );
  }

  const ghost = reference ? alignAtHip(reference, joints.hip) : null;
  const ear = joints.ear.ok ? project(joints.ear) : null;
  const shoulder = joints.shoulder.ok ? project(joints.shoulder) : null;
  const hip = joints.hip.ok ? project(joints.hip) : null;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full" role="img" aria-label="Skeleton at this moment">
      {ghost ? <Bones joints={ghost} project={project} stroke="var(--color-ink)" opacity={0.14} width={9} /> : null}

      {/* Guides: level at the shoulder (the neck angle's horizontal runs through the C7 estimate just above and behind it), upright at the hip. */}
      <g stroke="var(--color-muted)" strokeWidth={1} strokeDasharray="3 4" opacity={0.7}>
        {shoulder ? <line x1={shoulder.x} y1={shoulder.y} x2={shoulder.x + 60} y2={shoulder.y} /> : null}
        {hip && shoulder ? <line x1={hip.x} y1={hip.y} x2={hip.x} y2={hip.y - Math.hypot(shoulder.x - hip.x, shoulder.y - hip.y)} /> : null}
      </g>

      <Bones joints={joints} project={project} stroke={color} opacity={1} width={6} />
      {ear ? <circle cx={ear.x} cy={ear.y} r={headR} fill="var(--color-card)" stroke={color} strokeWidth={6} /> : null}

      {ear && shoulder && cva !== null ? (
        <Tag x={Math.max(ear.x + headR, shoulder.x) + 10} y={(ear.y + shoulder.y) / 2} text={`Neck ${cva.toFixed(0)}°`} color={color} />
      ) : null}
      {hip && shoulder && trunk !== null ? (
        // Behind the back: the arm is in front of the trunk.
        <Tag x={Math.min(hip.x, shoulder.x) - 18} y={(hip.y + shoulder.y) / 2} text={`Lean ${trunk.toFixed(0)}°`} color={color} anchor="end" />
      ) : null}
    </svg>
  );
}

function Bones({
  joints,
  project,
  stroke,
  opacity,
  width,
}: {
  joints: Joints;
  project(p: Point): { x: number; y: number };
  stroke: string;
  opacity: number;
  width: number;
}): ReactNode {
  return (
    <g stroke={stroke} strokeWidth={width} strokeLinecap="round" opacity={opacity}>
      {BONES.filter(([a, b]) => joints[a].ok && joints[b].ok).map(([a, b]) => {
        const p = project(joints[a]);
        const q = project(joints[b]);
        return <line key={`${a}-${b}`} x1={p.x} y1={p.y} x2={q.x} y2={q.y} />;
      })}
    </g>
  );
}

function Tag({
  x,
  y,
  text,
  color,
  anchor = 'start',
}: {
  x: number;
  y: number;
  text: string;
  color: string;
  anchor?: 'start' | 'end';
}): ReactNode {
  const width = text.length * 6.6 + 14;
  // Keep the tag inside the stage when the person sits near an edge.
  const left = Math.max(4, Math.min(anchor === 'end' ? x - width : x, W - width - 4));
  return (
    <g transform={`translate(${left} ${y - 11})`}>
      <rect width={width} height={22} rx={11} fill="var(--color-card)" stroke={color} strokeWidth={1.5} />
      <text x={width / 2} y={15} textAnchor="middle" fontSize={12} fontWeight={700} fill={color}>
        {text}
      </text>
    </g>
  );
}

/** h:mm:ss for an hour or more, m:ss below. */
function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}
